import { type LDClient, type LDFlagValue, initialize } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

export type { LDFlagValue };

interface UseFeatureFlagsForOrgResult {
  /** Every flag LaunchDarkly resolved for the org, keyed by flag key. Empty until resolved. */
  values: Record<string, LDFlagValue>;
  isLoading: boolean;
  error: Error | null;
}

type Action =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'resolved'; values: Record<string, LDFlagValue> }
  | { type: 'failed'; error: Error };

const EMPTY_VALUES: Record<string, LDFlagValue> = {};

function reducer(state: UseFeatureFlagsForOrgResult, action: Action): UseFeatureFlagsForOrgResult {
  switch (action.type) {
    case 'idle':
      return !state.isLoading && state.error === null && state.values === EMPTY_VALUES
        ? state
        : { values: EMPTY_VALUES, isLoading: false, error: null };
    case 'loading':
      return state.isLoading && state.error === null && state.values === EMPTY_VALUES
        ? state
        : { values: EMPTY_VALUES, isLoading: true, error: null };
    case 'resolved':
      return { values: action.values, isLoading: false, error: null };
    case 'failed':
      return { values: EMPTY_VALUES, isLoading: false, error: action.error };
  }
}

function initialState(orgId: string | undefined): UseFeatureFlagsForOrgResult {
  return { values: EMPTY_VALUES, isLoading: Boolean(orgId), error: null };
}

/**
 * Evaluate every feature flag against an arbitrary organization context,
 * independent of the logged-in user's LaunchDarkly client.
 *
 * Spins up a single ephemeral LaunchDarkly client scoped to the given org and
 * reads the full flag set off it with `allFlags()`. The client is closed on
 * unmount. Only flags marked "available to client-side SDKs" in LaunchDarkly
 * are included.
 *
 * The client is created with `sendEvents: false`. By default the SDK records
 * an evaluation event for every flag `allFlags()` reads, plus an identify
 * event for the synthetic context, so an admin viewing the org page would
 * show up in that org's flag insights and keep every flag looking recently
 * used. A read-only admin panel should record nothing.
 *
 * Caveat: only `organizationId` is sent on the LD context. If a flag's
 * targeting rule references attributes beyond `organizationId` (email,
 * workspaceId, segment membership), the value here will diverge from what real
 * users in that org actually see.
 *
 * The returned state belongs to the `orgId` it was resolved for. A consumer
 * whose `orgId` can change while mounted should key itself by it, otherwise
 * the previous org's values are visible for the one render before the effect
 * dispatches `loading`.
 */
export function useFeatureFlagsForOrg(orgId: string | undefined): UseFeatureFlagsForOrgResult {
  const [state, dispatch] = React.useReducer(reducer, orgId, initialState);

  React.useEffect(() => {
    if (!orgId) {
      dispatch({ type: 'idle' });
      return;
    }

    const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;
    if (!clientSideId) {
      dispatch({
        type: 'failed',
        error: new Error('Missing LaunchDarkly client-side ID (VITE_LAUNCHDARKLY_CLIENT_ID)'),
      });
      return;
    }

    let cancelled = false;
    dispatch({ type: 'loading' });

    const client: LDClient = initialize(
      clientSideId,
      {
        kind: 'user',
        key: `org-${orgId}`,
        organizationId: orgId,
      },
      { sendEvents: false },
    );

    client
      .waitForInitialization(5)
      .then(() => {
        if (cancelled) return;
        dispatch({ type: 'resolved', values: client.allFlags() });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        dispatch({ type: 'failed', error: err instanceof Error ? err : new Error(String(err)) });
      });

    return () => {
      cancelled = true;
      void client.close();
    };
  }, [orgId]);

  return state;
}
